import { toISODate } from '~/features/shared/calendar/dateUtils';
import type {
  CompType,
  ListingKind,
  ListingStatus,
  ServiceListing,
  ShiftLabel,
  WorkLocation,
} from './types';

export type ProviderServiceListingApi = {
  activeFlag?: boolean;
  aois?: Array<{
    [key: string]: unknown;
    aoiName?: string | null;
    aoiSpecialtyId?: number | null;
    areaOfInterestId: number;
    id?: number;
  }>;
  description?: string | null;
  /** False when the provider wants subspecialty work only (MCV-1855). */
  generalSpecialtyFlag?: boolean;
  id: number;
  listingTitle: string;
  listingKind: ListingKind;
  providerCategoryId?: number;
  status: ListingStatus;
  timezone?: string;
  visibility?: 'PUBLIC' | 'PRIVATE';
  workLocation: WorkLocation;
  publishValidationErrors?: string[];
  terms?: {
    [key: string]: unknown;
    compType?: CompType;
    hoursPerShift?: string | number | null;
    pricePerCallShift?: string | number | null;
    pricePerHour?: string | number | null;
    pricePerRvu?: string | number | null;
    pricePerShift?: string | number | null;
    startDate?: string | null;
  } | null;
  licenseStates?: Array<{ id?: number; stateCode: string }>;
  geographies?: Array<{
    [key: string]: unknown;
    city?: string | null;
    id?: number;
    latitude?: string | number | null;
    longitude?: string | number | null;
    preferenceType?: 'CITY' | 'ZIP';
    radiusMiles?: string | number | null;
    sourceZipGeographyId?: number | null;
    stateCode?: string | null;
    zipCode?: string | null;
  }>;
  specialties?: Array<{
    [key: string]: unknown;
    aoiSpecialtyId?: number | null;
    id?: number;
    specialtyName?: string | null;
  }>;
  availabilitySlots?: Array<{ startsAt?: string; status?: string }>;
  recurringAvailabilityRules?: Array<{
    effectiveStartDate?: string;
    shiftLabel?: ShiftLabel;
  }>;
  workPreference?: {
    [key: string]: unknown;
    callPreference?: FormState['callPreference'] | null;
    maxShiftsPerMonth?: number | null;
    maxShiftsPerWeek?: number | null;
    preferredShiftLabels?: ShiftLabel[] | null;
    preferredStartDate?: string | null;
    scheduleNotes?: string | null;
    willingToTakeCall?: boolean | null;
  } | null;
};

export type AoiSpecialtyApi = {
  aoiSpecialtyName: string;
  id: number;
  providerCategoryId: number;
};

export type ProviderServiceListingPayload = {
  aois: Array<{ areaOfInterestId: number }>;
  description?: string;
  generalSpecialtyFlag: boolean;
  geographies: Array<{
    [key: string]: unknown;
    city?: string | null;
    latitude?: number | string | null;
    longitude?: number | string | null;
    preferenceType?: GeographyMode;
    radiusMiles?: number | string | null;
    sourceZipGeographyId?: number | null;
    stateCode?: string | null;
    zipCode?: string | null;
  }>;
  licenseStates: Array<{ id?: number; stateCode: string }>;
  listingKind: ListingKind;
  listingTitle: string;
  providerCategoryId: number;
  specialties: Array<{
    [key: string]: unknown;
    aoiSpecialtyId: number;
    specialtyName: string;
  }>;
  status: ListingStatus;
  terms: {
    [key: string]: unknown;
    compType: CompType;
    hoursPerShift?: number;
    pricePerCallShift?: number;
    pricePerHour?: number;
    pricePerRvu?: number;
    pricePerShift?: number;
    ratesNegotiableFlag: true;
    startDate: string;
  };
  timezone: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  workLocation: WorkLocation;
  workPreference?: {
    [key: string]: unknown;
    callPreference: FormState['callPreference'];
    maxShiftsPerMonth?: number;
    maxShiftsPerWeek?: number;
    preferredShiftLabels: ShiftLabel[];
    preferredStartDate: string;
    scheduleNotes: string;
    willingToTakeCall: boolean;
  } | null;
};

export type GeographyMode = 'ZIP' | 'CITY';

/** A location row from `GET /geographies/search`: a ZIP, or one city with a representative ZIP. */
export type GeographyOption = {
  city: string;
  id: number;
  kind: GeographyMode;
  label: string;
  latitude: number;
  longitude: number;
  state: string;
  zip: string;
};

/** A subspecialty (area of interest) picked under one of the listing's specialties. */
export type SelectedSubspecialty = {
  aoiName: string;
  aoiSpecialtyId: number;
  areaOfInterestId: number;
};

export type FormState = {
  aoiSpecialties: string[];
  callPreference: 'NONE' | 'LIMITED' | 'REGULAR' | 'ANY';
  compType: CompType;
  customSpecialty: string;
  /** Only meaningful with a subspecialty picked; the payload sends true without one. */
  generalSpecialtyFlag: boolean;
  /** What the Location box shows: the picked label, or a saved free-text value kept as it was. */
  geographyQuery: string;
  /** The picked reference row; null for an empty box or a legacy free-text value. */
  geographySelection: GeographyOption | null;
  hoursPerShift: string;
  kind: ListingKind;
  licenseStates: string[];
  maxShiftsPerMonth: string;
  maxShiftsPerWeek: string;
  preferredStartDate: string;
  pricePerCallShift: string;
  pricePerHour: string;
  pricePerRvu: string;
  pricePerShift: string;
  radiusMiles: string;
  scheduleNotes: string;
  shiftLabels: ShiftLabel[];
  specialtyIdsByName: Record<string, number>;
  subspecialties: SelectedSubspecialty[];
  title: string;
  workLocation: WorkLocation;
};

export type ExpectedRateField =
  | 'hoursPerShift'
  | 'pricePerCallShift'
  | 'pricePerHour'
  | 'pricePerRvu'
  | 'pricePerShift';

export type ExpectedRateErrors = Partial<Record<ExpectedRateField, string>>;

export const LISTING_KIND_OPTIONS: Array<{
  description: string;
  label: string;
  value: ListingKind;
}> = [
  {
    description: 'Concrete calendar availability for temporary coverage.',
    label: 'Locums',
    value: 'LOCUMS',
  },
  {
    description: 'Recurring weekly availability with shift limits.',
    label: 'Part-time',
    value: 'PART_TIME',
  },
  {
    description: 'General availability for permanent or full-time work.',
    label: 'Full-time',
    value: 'FULL_TIME',
  },
];

export const WORK_LOCATION_OPTIONS: Array<{ label: string; value: WorkLocation }> = [
  { label: 'Onsite', value: 'ONSITE' },
  { label: 'Hybrid', value: 'PARTIAL_REMOTE' },
  { label: 'Remote', value: 'REMOTE' },
];

export const SHIFT_LABEL_OPTIONS: Array<{ label: string; value: ShiftLabel }> = [
  { label: 'Day', value: 'DAY' },
  { label: 'Evening', value: 'EVENING' },
  { label: 'Night', value: 'NIGHT' },
];

export const INITIAL_FORM_STATE: FormState = {
  aoiSpecialties: ['Emergency Medicine'],
  callPreference: 'LIMITED',
  compType: 'HOUR',
  customSpecialty: '',
  generalSpecialtyFlag: true,
  geographyQuery: '',
  geographySelection: null,
  hoursPerShift: '10',
  kind: 'PART_TIME',
  // [MCV-1864] A new listing starts with no license states; the provider picks from the states
  // they are currently licensed in.
  licenseStates: [],
  maxShiftsPerMonth: '8',
  maxShiftsPerWeek: '2',
  preferredStartDate: toISODate(new Date()),
  pricePerCallShift: '700',
  pricePerHour: '240',
  pricePerRvu: '',
  pricePerShift: '',
  radiusMiles: '50',
  scheduleNotes: 'Flexible on specific weekdays when matched with advance notice.',
  shiftLabels: ['DAY'],
  specialtyIdsByName: {},
  subspecialties: [],
  title: 'Emergency and hospitalist coverage',
  workLocation: 'PARTIAL_REMOTE',
};

export const formatKind = (kind: ListingKind) =>
  LISTING_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;

export const formatWorkLocation = (workLocation: WorkLocation) =>
  WORK_LOCATION_OPTIONS.find((option) => option.value === workLocation)?.label ?? workLocation;

export const parseNonnegativeNumber = (value: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

const compactNumber = (value: string) => parseNonnegativeNumber(value);

const validateNumericField = (value: string, label: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return `Enter a valid ${label.toLowerCase()}.`;
  if (parsed < 0) return `${label} cannot be negative.`;
  return undefined;
};

export const validateExpectedRates = (formState: FormState): ExpectedRateErrors => {
  const errors: ExpectedRateErrors = {};
  const primaryRateField: [ExpectedRateField, string] =
    formState.compType === 'HOUR'
      ? ['pricePerHour', 'Expected hourly rate']
      : formState.compType === 'SHIFT'
        ? ['pricePerShift', 'Expected shift rate']
        : ['pricePerRvu', 'Expected RVU rate'];
  const fields: Array<[ExpectedRateField, string]> = [
    ['hoursPerShift', 'Hours per shift'],
    ['pricePerCallShift', 'Expected call rate'],
    primaryRateField,
  ];

  fields.forEach(([field, label]) => {
    const error = validateNumericField(formState[field], label);
    if (error) errors[field] = error;
  });
  return errors;
};

/** The radius must be positive (the DB requires it); it is optional, and unused for a remote listing. */
export const validateRadius = (formState: FormState) => {
  if (formState.workLocation === 'REMOTE' || !formState.radiusMiles.trim()) return undefined;
  const parsed = Number(formState.radiusMiles);
  if (!Number.isFinite(parsed)) return 'Enter a valid radius.';
  if (parsed <= 0) return 'Radius must be greater than 0.';
  return undefined;
};

const selectedSpecialtyNames = (formState: FormState) => [
  ...new Set([...formState.aoiSpecialties, formState.customSpecialty.trim()].filter(Boolean)),
];

const resolveSpecialty = (formState: FormState, requestedName: string) => {
  const canonicalName = Object.keys(formState.specialtyIdsByName).find(
    (name) => name.toLocaleLowerCase() === requestedName.toLocaleLowerCase()
  );
  if (!canonicalName) return undefined;
  return {
    aoiSpecialtyId: formState.specialtyIdsByName[canonicalName],
    specialtyName: canonicalName,
  };
};

export const validateSpecialtySelections = (formState: FormState) => {
  const unresolved = selectedSpecialtyNames(formState).filter(
    (specialtyName) => !resolveSpecialty(formState, specialtyName)
  );
  if (unresolved.length === 0) return undefined;
  return `Choose specialties from the available canonical options. Unrecognized: ${unresolved.join(
    ', '
  )}.`;
};

/**
 * The specialties on the listing, as `{ id, name }` in the order picked: the Specialties box plus
 * a canonical name typed in Additional AOI Specialty (the same set validation and the payload use).
 */
export const pickedSpecialties = (formState: FormState) => {
  const seen = new Set<number>();
  return selectedSpecialtyNames(formState).flatMap((name) => {
    const resolved = resolveSpecialty(formState, name);
    if (!resolved || seen.has(resolved.aoiSpecialtyId)) return [];
    seen.add(resolved.aoiSpecialtyId);
    return [{ id: resolved.aoiSpecialtyId, name: resolved.specialtyName }];
  });
};

/**
 * [MCV-1855] Removing a specialty drops the subspecialties picked under it. The flag falls back to
 * its default once none is left, so a later pick starts from "open to general work".
 */
export const withoutOrphanedSubspecialties = (formState: FormState): FormState => {
  const ids = new Set(pickedSpecialties(formState).map((specialty) => specialty.id));
  const subspecialties = formState.subspecialties.filter((subspecialty) =>
    ids.has(subspecialty.aoiSpecialtyId)
  );
  if (subspecialties.length === formState.subspecialties.length) return formState;
  return {
    ...formState,
    generalSpecialtyFlag: subspecialties.length ? formState.generalSpecialtyFlag : true,
    subspecialties,
  };
};

// One box takes either a ZIP or "City, ST", so the mode follows what was typed.
const inferGeographyMode = (query: string): GeographyMode =>
  /^\d{5}(-\d{4})?$/.test(query.trim()) ? 'ZIP' : 'CITY';

const parseLocation = (query: string, mode: GeographyMode) => {
  const trimmed = query.trim();
  if (mode === 'ZIP') {
    return { zipCode: trimmed.slice(0, 5) };
  }
  // "Norwalk OH" (no comma) is the same place as "Norwalk, OH".
  const spaced = trimmed.includes(',') ? null : /^(.*\S)\s+([A-Za-z]{2})$/.exec(trimmed);
  const [cityPart, statePart] = spaced
    ? [spaced[1], spaced[2]]
    : trimmed.split(',').map((part) => part.trim());
  return {
    city: cityPart || undefined,
    stateCode: statePart?.slice(0, 2).toUpperCase() || undefined,
  };
};

// What one geography row stores for the current Location box. A picked location sends its
// reference row and centroid (the API re-derives the point from the reference row); an emptied box
// clears all of it; a saved free-text value is re-sent as the text it was, now split on a space as
// well as a comma, and keeps whatever the row already had.
const geographyFields = (formState: FormState) => {
  const selection = formState.geographySelection;
  if (selection) {
    return {
      city: selection.city,
      latitude: selection.latitude,
      longitude: selection.longitude,
      preferenceType: selection.kind,
      sourceZipGeographyId: selection.id,
      stateCode: selection.state,
      zipCode: selection.kind === 'ZIP' ? selection.zip : null,
    };
  }
  if (!formState.geographyQuery.trim()) {
    return {
      city: null,
      latitude: null,
      longitude: null,
      preferenceType: 'CITY' as const,
      sourceZipGeographyId: null,
      stateCode: null,
      zipCode: null,
    };
  }
  const mode = inferGeographyMode(formState.geographyQuery);
  const { city, stateCode } = parseLocation(formState.geographyQuery, 'CITY');
  // [MCV-1764] buildPayload spreads the saved first geography under these fields, so a re-typed
  // free-text location has to null the coordinates and source row explicitly; leaving them
  // undefined would keep the previous place's latitude/longitude on the new text.
  return {
    city: mode === 'CITY' ? city : undefined,
    latitude: null,
    longitude: null,
    sourceZipGeographyId: null,
    preferenceType: mode,
    stateCode: mode === 'CITY' ? stateCode : undefined,
    zipCode: mode === 'ZIP' ? parseLocation(formState.geographyQuery, 'ZIP').zipCode : undefined,
  };
};

const formatRate = (terms?: ProviderServiceListingApi['terms']) => {
  if (!terms?.compType) return 'Rates negotiable';
  const value =
    terms.compType === 'HOUR'
      ? terms.pricePerHour
      : terms.compType === 'SHIFT'
        ? terms.pricePerShift
        : terms.pricePerRvu;
  const suffix = terms.compType === 'HOUR' ? 'hr' : terms.compType === 'SHIFT' ? 'shift' : 'RVU';
  return value ? `$${value}/${suffix} expected, negotiable` : 'Rates negotiable';
};

export const mapApiListing = (listing: ProviderServiceListingApi): ServiceListing => {
  const firstGeography = listing.geographies?.[0];
  const specialtyNames =
    listing.specialties
      ?.map((specialty) => specialty.specialtyName)
      .filter((specialty): specialty is string => Boolean(specialty)) ?? [];
  const licenseStates = listing.licenseStates?.map((state) => state.stateCode) ?? [];
  const subspecialties = [
    ...new Set(
      listing.aois?.map((aoi) => aoi.aoiName).filter((name): name is string => Boolean(name)) ?? []
    ),
  ].sort((left, right) => left.localeCompare(right));
  const geography =
    listing.workLocation === 'REMOTE'
      ? 'Remote'
      : firstGeography
        ? `${firstGeography.city || firstGeography.zipCode || 'Location'}${
            firstGeography.stateCode ? `, ${firstGeography.stateCode}` : ''
          } within ${firstGeography.radiusMiles || 0} mi`
        : 'Not set';

  return {
    aoiSpecialties: specialtyNames.length ? specialtyNames : ['AOI specialties pending'],
    availabilitySummary: 'Managed in Calendar',
    compSummary: formatRate(listing.terms),
    generalSpecialtyFlag: listing.generalSpecialtyFlag ?? true,
    geography,
    id: listing.id,
    inquiryCount: 0,
    kind: listing.listingKind,
    licenseStates,
    nextAvailability: 'Open Calendar',
    publishRequirements:
      listing.specialties?.length === 0
        ? ['Select Edit to add at least one specialty before publishing.']
        : [],
    status: listing.status,
    subspecialties,
    title: listing.listingTitle,
    workLocation: listing.workLocation,
  };
};

const formNumber = (value: string | number | null | undefined) =>
  value == null ? '' : String(value);

export const geographyLabel = (kind: GeographyMode, city: string, state: string, zip: string) =>
  kind === 'ZIP' ? (city && state ? `${city}, ${state} ${zip}` : zip) : `${city}, ${state}`;

// A saved location that was picked from the list carries its reference row; one saved before
// the picker existed does not, and is shown (and re-saved) as the text it was.
const savedGeographySelection = (
  geography: NonNullable<ProviderServiceListingApi['geographies']>[number] | undefined
): GeographyOption | null => {
  if (geography?.sourceZipGeographyId == null) return null;
  const kind = geography.preferenceType ?? 'CITY';
  const city = geography.city ?? '';
  const state = geography.stateCode ?? '';
  const zip = geography.zipCode ?? '';
  return {
    city,
    id: geography.sourceZipGeographyId,
    kind,
    label: geographyLabel(kind, city, state, zip),
    latitude: Number(geography.latitude),
    longitude: Number(geography.longitude),
    state,
    zip,
  };
};

export const mapApiListingToFormState = (listing: ProviderServiceListingApi): FormState => {
  const firstGeography = listing.geographies?.[0];
  const specialtyNames = [
    ...new Set(
      listing.specialties
        ?.map((specialty) => specialty.specialtyName?.trim())
        .filter((name): name is string => Boolean(name)) ?? []
    ),
  ];
  const specialtyIdsByName = Object.fromEntries(
    listing.specialties
      ?.filter(
        (specialty): specialty is { aoiSpecialtyId: number; specialtyName: string } =>
          specialty.aoiSpecialtyId != null && Boolean(specialty.specialtyName)
      )
      .map((specialty) => [specialty.specialtyName.trim(), specialty.aoiSpecialtyId]) ?? []
  );
  const workPreference = listing.workPreference;
  const geographyMode = firstGeography?.preferenceType ?? 'CITY';
  const geographySelection = savedGeographySelection(firstGeography);
  const geographyQuery =
    geographySelection?.label ??
    (geographyMode === 'ZIP'
      ? (firstGeography?.zipCode ?? '')
      : [firstGeography?.city, firstGeography?.stateCode].filter(Boolean).join(', '));

  return {
    aoiSpecialties: specialtyNames,
    callPreference:
      workPreference?.callPreference ??
      (workPreference?.willingToTakeCall === false ? 'NONE' : 'LIMITED'),
    compType: listing.terms?.compType ?? 'HOUR',
    customSpecialty: '',
    generalSpecialtyFlag: listing.generalSpecialtyFlag ?? true,
    geographyQuery,
    geographySelection,
    hoursPerShift: formNumber(listing.terms?.hoursPerShift),
    kind: listing.listingKind,
    licenseStates: listing.licenseStates?.map((state) => state.stateCode) ?? [],
    maxShiftsPerMonth: formNumber(workPreference?.maxShiftsPerMonth),
    maxShiftsPerWeek: formNumber(workPreference?.maxShiftsPerWeek),
    preferredStartDate: workPreference?.preferredStartDate ?? listing.terms?.startDate ?? '',
    pricePerCallShift: formNumber(listing.terms?.pricePerCallShift),
    pricePerHour: formNumber(listing.terms?.pricePerHour),
    pricePerRvu: formNumber(listing.terms?.pricePerRvu),
    pricePerShift: formNumber(listing.terms?.pricePerShift),
    radiusMiles: formNumber(firstGeography?.radiusMiles),
    scheduleNotes: workPreference?.scheduleNotes ?? listing.description ?? '',
    shiftLabels: workPreference?.preferredShiftLabels ?? [],
    specialtyIdsByName,
    subspecialties:
      listing.aois?.flatMap((aoi) =>
        aoi.aoiSpecialtyId != null && aoi.aoiName
          ? [
              {
                aoiName: aoi.aoiName,
                aoiSpecialtyId: aoi.aoiSpecialtyId,
                areaOfInterestId: aoi.areaOfInterestId,
              },
            ]
          : []
      ) ?? [],
    title: listing.listingTitle,
    workLocation: listing.workLocation,
  };
};

export const buildPayload = (
  formState: FormState,
  providerCategoryId: number,
  source?: ProviderServiceListingApi | null
): ProviderServiceListingPayload => {
  const specialtyError = validateSpecialtySelections(formState);
  if (specialtyError) throw new Error(specialtyError);

  const preservedTerms = Object.fromEntries(
    Object.entries(source?.terms ?? {}).filter(
      ([key]) =>
        ![
          'compType',
          'hoursPerShift',
          'pricePerCallShift',
          'pricePerHour',
          'pricePerRvu',
          'pricePerShift',
          'ratesNegotiableFlag',
          'startDate',
        ].includes(key)
    )
  );
  const primaryRate =
    formState.compType === 'HOUR'
      ? { pricePerHour: compactNumber(formState.pricePerHour) }
      : formState.compType === 'SHIFT'
        ? { pricePerShift: compactNumber(formState.pricePerShift) }
        : { pricePerRvu: compactNumber(formState.pricePerRvu) };
  const terms = {
    ...preservedTerms,
    compType: formState.compType,
    hoursPerShift: compactNumber(formState.hoursPerShift),
    pricePerCallShift: compactNumber(formState.pricePerCallShift),
    ...primaryRate,
    ratesNegotiableFlag: true as const,
    startDate: formState.preferredStartDate,
  };
  const specialties = selectedSpecialtyNames(formState).map((specialtyName) => {
    const resolved = resolveSpecialty(formState, specialtyName);
    if (!resolved) throw new Error(`Unrecognized specialty: ${specialtyName}`);
    const existing = source?.specialties?.find(
      (specialty) => specialty.aoiSpecialtyId === resolved.aoiSpecialtyId
    );
    return { ...existing, ...resolved };
  });
  const licenseStates = formState.licenseStates.map((stateCode) => ({
    ...source?.licenseStates?.find((state) => state.stateCode === stateCode),
    stateCode,
  }));
  // [MCV-1855] The server derives the name and specialty of each subspecialty from its id. Only
  // subspecialties under a specialty that is still on the listing are sent.
  const specialtyIds = new Set(specialties.map((specialty) => specialty.aoiSpecialtyId));
  const aois = formState.subspecialties
    .filter((subspecialty) => specialtyIds.has(subspecialty.aoiSpecialtyId))
    .map((subspecialty) => ({ areaOfInterestId: subspecialty.areaOfInterestId }));
  const payload: ProviderServiceListingPayload = {
    aois,
    description:
      source && formState.kind !== 'LOCUMS'
        ? (source.description ?? formState.scheduleNotes)
        : formState.scheduleNotes,
    // The checkbox only shows once a subspecialty is picked; without one the listing is general.
    generalSpecialtyFlag: aois.length ? formState.generalSpecialtyFlag : true,
    geographies: [],
    licenseStates,
    listingKind: formState.kind,
    listingTitle: formState.title,
    providerCategoryId: source?.providerCategoryId ?? providerCategoryId,
    specialties,
    status: source?.status ?? 'DRAFT',
    terms,
    timezone:
      source?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/New_York',
    visibility: source?.visibility ?? 'PUBLIC',
    workLocation: formState.workLocation,
  };

  if (formState.workLocation !== 'REMOTE') {
    const [firstGeography, ...additionalGeographies] = source?.geographies ?? [];
    payload.geographies = [
      {
        ...firstGeography,
        ...geographyFields(formState),
        radiusMiles: compactNumber(formState.radiusMiles),
      },
      ...additionalGeographies,
    ];
  }

  if (formState.kind !== 'LOCUMS') {
    const preservedWorkPreference = Object.fromEntries(
      Object.entries(source?.workPreference ?? {}).filter(
        ([key]) =>
          ![
            'callPreference',
            'maxShiftsPerMonth',
            'maxShiftsPerWeek',
            'preferredShiftLabels',
            'preferredStartDate',
            'scheduleNotes',
            'willingToTakeCall',
          ].includes(key)
      )
    );
    payload.workPreference = {
      ...preservedWorkPreference,
      callPreference: formState.callPreference,
      ...(formState.kind === 'PART_TIME'
        ? {
            maxShiftsPerMonth: compactNumber(formState.maxShiftsPerMonth),
            maxShiftsPerWeek: compactNumber(formState.maxShiftsPerWeek),
          }
        : {}),
      preferredShiftLabels: formState.shiftLabels,
      preferredStartDate: formState.preferredStartDate,
      scheduleNotes: formState.scheduleNotes,
      willingToTakeCall: formState.callPreference !== 'NONE',
    };
  } else if (source) {
    payload.workPreference = null;
  }

  return payload;
};

export const toggleValue = <T extends string>(values: T[], value: T) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
